import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions, type Permission } from "@/hooks/use-permissions";
import { useHasTier, useHasFeature } from "@/hooks/use-tenant";
import { type TenantUserRole } from "@/lib/auth";
import { type SubscriptionTier, type TierFeature } from "@/lib/constants";
import { Loader2, ShieldX, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPortal?: string;
  requiredRole?: TenantUserRole;
  requiredPermission?: Permission;
  requiredPermissions?: Permission[];
  requiredTier?: SubscriptionTier;
  requiredFeature?: TierFeature;
}

export function ProtectedRoute({
  children,
  requiredPortal,
  requiredRole,
  requiredPermission,
  requiredPermissions,
  requiredTier,
  requiredFeature,
}: ProtectedRouteProps) {
  const { user, isLoading, checkPortalAccess } = useAuth();
  const { hasRole, can, canAll } = usePermissions();
  const hasTier = useHasTier(requiredTier || "receptionist");
  const hasFeature = useHasFeature(requiredFeature || "aiReceptionist");
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to login with return URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check portal access
  if (requiredPortal && !checkPortalAccess(requiredPortal)) {
    return <AccessDeniedPage message="You don't have access to this portal." />;
  }

  // Check role requirement
  if (requiredRole && !hasRole(requiredRole)) {
    return <AccessDeniedPage message={`This page requires ${requiredRole} access or higher.`} />;
  }

  // Check single permission
  if (requiredPermission && !can(requiredPermission)) {
    return <AccessDeniedPage message="You don't have permission to access this page." />;
  }

  // Check multiple permissions (all required)
  if (requiredPermissions && !canAll(requiredPermissions)) {
    return <AccessDeniedPage message="You don't have the required permissions for this page." />;
  }

  // Check subscription tier requirement
  if (requiredTier && !hasTier) {
    const tierNames: Record<SubscriptionTier, string> = {
      receptionist: "Core",
      starter: "Core",
      outbound: "Professional",
      complete: "Complete",
    };
    return (
      <UpgradeRequiredPage
        feature="this feature"
        requiredTier={tierNames[requiredTier] || requiredTier}
      />
    );
  }

  // Check feature requirement
  if (requiredFeature && !hasFeature) {
    const featureNames: Record<TierFeature, string> = {
      aiReceptionist: "AI Receptionist",
      businessDashboard: "Business Dashboard",
      googleCalendar: "Google Calendar",
      techPortal: "Tech Portal",
      voiceToText: "Voice-to-Text Notes",
      smartDispatch: "Smart Dispatch",
      equipmentSurvey: "Equipment Survey",
      maxTechnicians: "Technician Management",
    };
    return (
      <UpgradeRequiredPage
        feature={featureNames[requiredFeature] || requiredFeature}
        requiredTier="Professional"
      />
    );
  }

  return <>{children}</>;
}

/**
 * Access Denied page component
 */
function AccessDeniedPage({ message }: { message: string }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <ShieldX className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
        <p className="text-muted-foreground mb-6">{message}</p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => window.history.back()}>
            Go Back
          </Button>
          <Button asChild>
            <a href="/app/dashboard">Dashboard</a>
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Upgrade Required page component for tier-gated features
 */
function UpgradeRequiredPage({ feature, requiredTier }: { feature: string; requiredTier: string }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">Upgrade Required</h1>
        <p className="text-muted-foreground mb-2">
          <strong>{feature}</strong> is available on the <strong>{requiredTier}</strong> plan and above.
        </p>
        <p className="text-sm text-muted-foreground mb-6">
          Upgrade your subscription to unlock this feature and more.
        </p>
        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => window.history.back()}>
            Go Back
          </Button>
          <Button asChild>
            <a href="/pricing">View Plans</a>
          </Button>
        </div>
      </div>
    </div>
  );
}
